<template>
  <overlay
    v-if="mobileNavigation"
    key="mobilePageNavigation"
    direction="up"
    namespace="mobilePageNavigation"
    :template="false"
    type="shelf"
  >
    <template #body="{ isActive }">
      <ul
        :is-active="isActive"
        class="page-navigation__mobile-list"
      >
        <li
          v-for="(item, index) of mobileNavigation.items"
          :key="`mobile-page-navigation-${index}`"
          class="page-navigation__mobile-item"
          :class="item.active && 'page-navigation__mobile-item--active'"
        >
          <a
            class="text-mobile-16-mobile"
            :href="item.url"
            v-text="item.title"
          />
        </li>
      </ul>
    </template>
  </overlay>
</template>

<script>

/**
 * Vue: Main page (main-page)
 * -----------------------------------------------------------------------------
 * Page template.
 *
 * @param {String} liquid.sectionId - Section's Liquid ID.
 *
 */

import { mapActions } from 'vuex'

import Overlay from '~global/overlay/overlay'

export default {
  name: 'MainPage',

  components: {
    Overlay,
  },

  props: {
    pageBackground: {
      type: String,
      default: '',
    },

    mobileNavigation: {
      type: [Boolean, Object],
      default: false,
    },
  },

  mounted() {
    // @TODO: Canvas isn't loaded when pages are loaded via Ajax.
    // So we need to make a fix for that.
    // this.bindAjaxLinks()
    this.setNodeSelectors()
    this.setPageBackground()
    this.handlePageResize()
    this.handleReadMoreButton(this.nodeSelectors.page.readMore)
    this.handleMobileNavigation(this.nodeSelectors.page.mobileNavigation)

    const ingridTrigger = this.nodeSelectors.page.ingridTrigger
    if (ingridTrigger) {
      ingridTrigger.addEventListener('click', (event) => {
        event.preventDefault()
        cnvs.EventBus.emit('ingrid-returns-clicked')
      })
    }
  },

  methods: {

    /**
     * Map Vuex actions.
     */
    ...mapActions({
      openOverlay: 'overlays/open',
    }),

    bindAjaxLinks() {
      document.querySelector('.main-page').addEventListener('click', (event) => {
        const target = event.target

        if (!target.hasAttribute('ajax-navigation')) {
          return
        }

        event.preventDefault()
        this.loadPage(target.getAttribute('href'))
      })
    },

    async loadPage(url) {
      const currentPage = document.querySelector('.main-page')
      this.setLoading(currentPage, true)

      const response = await fetch(url)
      const responseText = await response.text()

      const html = await new DOMParser().parseFromString(responseText, 'text/html')
      const newPage = html.querySelector('.main-page')

      if (!newPage) {
        this.setLoading(currentPage, false)
        return
      }

      window.history.pushState({}, null, url)

      currentPage.outerHTML = newPage.outerHTML

      this.bindAjaxLinks()
      this.setNodeSelectors()
      this.setPageBackground()
      this.handlePageResize()
      this.handleReadMoreButton(this.nodeSelectors.page.readMore)
      this.handleMobileNavigation(this.nodeSelectors.page.mobileNavigation)

      this.setLoading(currentPage, false)
    },

    setLoading(section, loading) {
      section.classList.toggle('main-page--loading', loading)
    },

    /**
     * Set node selectors.
     */
    setNodeSelectors() {
      this.nodeSelectors = {
        navigation: {
          main: document.querySelector('[js-sticky-navigation="main"]'),
          sub: document.querySelector('[js-sticky-navigation="sub"]'),
          ctas: document.querySelector('[js-sticky-navigation="ctas"]'),
        },
        page: {
          container: document.querySelector('[js-main-page="container"]'),
          header: document.querySelector('[js-main-page="header"]'),
          readMore: document.querySelector('[js-main-page="read-more"]'),
          mobileNavigation: document.querySelector('[js-main-page="mobile-navigation"]'),
          ingridTrigger: document.querySelector('a[title="TOTEME Returns"]'),
        },
      }
    },

    /**
     * Sets page background setting as global variable.
     */
    setPageBackground() {
      document.documentElement.style.setProperty(
        '--page-bg',
        `${this.pageBackground}`,
      )
    },

    /**
     * Sets a resize observer to update page media height for full screen.
     */
    handlePageResize() {
      const self = this
      const container = this.nodeSelectors.page.container
      const mainNavigation = this.nodeSelectors.navigation.main
      const subNavigation = this.nodeSelectors.navigation.sub

      const resizeObserver = new ResizeObserver((entries) => {
        entries.forEach(() => {
          self.handleStickyNavigation(mainNavigation)
          self.handleStickyNavigation(subNavigation)
          self.handleStickyCtas()
        })
      })

      resizeObserver.observe(container)
    },

    /**
     * Set sticky navigation top value based on page header height
     * @param {Element} el - Navigation to make sticky.
     */
    handleStickyNavigation(el) {
      if (!el) {
        return
      }

      const header = this.nodeSelectors.page.header
      const headerHeight = header?.getBoundingClientRect().height || 0

      el.style.top = `${headerHeight}px`
    },

    /**
     * Set sticky ctas top value based on page header and sub-navigation
     */
    handleStickyCtas() {
      const stickyCtas = this.nodeSelectors.navigation.ctas

      if (!stickyCtas) {
        return
      }

      const header = this.nodeSelectors.page.header
      const headerHeight = header?.getBoundingClientRect().height || 0

      const subNav = this.nodeSelectors.navigation.sub
      const subNavHeight = subNav?.getBoundingClientRect().height || 0

      stickyCtas.style.top = `${headerHeight + subNavHeight}px`
    },

    /**
     * Handles scroll when clicking on read more button.
     */
    handleReadMoreButton(el) {
      if (!el) {
        return
      }

      el.addEventListener('click', () => {
        const block = el.closest('.main-page__block')
        const blockBottom = block?.getBoundingClientRect().bottom + window.scrollY

        window.scrollTo({
          top: blockBottom,
          behavior: 'smooth',
        })
      })
    },

    /**
     * Handles mobile navigation
     */
    handleMobileNavigation(el) {
      if (!el) {
        return
      }

      el.addEventListener('click', () => {
        this.openOverlay({
          ignoreDismissed: true,
          namespace: 'mobilePageNavigation',
        })
      })
    },
  },
}
</script>

<style lang="scss">
@import './main-page';
@import './page-header/page-header.scss';
@import './page-navigation/page-navigation.scss';
@import './page-career-list/page-career-list.scss';
@import './page-half-screen/page-half-screen.scss';
</style>
